import { StandardSnack } from '@components/snackbar';
import {
  TaskHistoryCommentIcon,
  TaskHistoryDateIcon,
  TaskHistoryDefaultIcon,
  TaskHistoryFieldtaskIcon,
  TaskHistoryMoveIcon,
  TaskHistoryMovetaskIcon,
  TaskHistoryNameIcon,
  TaskHistoryPriorityIcon,
  TaskHistoryRemoveIcon,
  TaskHistorySubtaskIcon,
  TaskHistorytaskAssigneeIcon,
  TaskHistorytaskAttachmentIcon,
  TaskHistorytaskCompleteIcon,
  TaskHistoryTaskIcon,
  TaskHistoryTimerIcon,
  TaskHistoryUpvoteIcon,
} from '@sketchnote/icons';
import { LoaderXs } from '@sketchnote/shared-components';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import * as React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { API } from 'src/api';

import { Task } from '../types/tasks';
const { useState, useEffect } = React;

const TaskIconWrapper = ({ children }: { children?: React.ReactNode }) => {
  return (
    <div
      className="w-32 h-32 flex-shrink-0 rounded-full flex items-center justify-center text-gray-900"
      style={{ backgroundColor: '#EEF1FC' }}
    >
      {children}
    </div>
  );
};

const EditTaskNameIcon = () => {
  return (
    <TaskIconWrapper>
      <TaskHistoryNameIcon />
    </TaskIconWrapper>
  );
};
const MoveToFileIcon = () => {
  return (
    <TaskIconWrapper>
      <TaskHistoryMoveIcon />
    </TaskIconWrapper>
  );
};
const UpvoteIcon = () => {
  return (
    <TaskIconWrapper>
      <TaskHistoryUpvoteIcon />
    </TaskIconWrapper>
  );
};
const RemoveTaskIcon = () => {
  return (
    <TaskIconWrapper>
      <TaskHistoryRemoveIcon />
    </TaskIconWrapper>
  );
};

const DefaultIcon = () => {
  return (
    <TaskIconWrapper>
      <TaskHistoryDefaultIcon />
    </TaskIconWrapper>
  );
};

const HistoryTypeIcon = {
  add_task: TaskHistoryTaskIcon,
  add_comment: TaskHistoryCommentIcon,
  edit_comment: TaskHistoryCommentIcon,
  remove_comment: TaskHistoryCommentIcon,
  add_attachment: TaskHistorytaskAttachmentIcon,
  add_subtask: TaskHistorySubtaskIcon,
  remove_subtask: TaskHistorySubtaskIcon,
  complete_subtask: TaskHistorytaskCompleteIcon,
  complete: TaskHistorytaskCompleteIcon,
  move_to_column: TaskHistoryMovetaskIcon,
  field: TaskHistoryFieldtaskIcon,
  add_assignee: TaskHistorytaskAssigneeIcon,
  remove_assignee: TaskHistorytaskAssigneeIcon,
  assigner: TaskHistorytaskAssigneeIcon,
  update_priority: TaskHistoryPriorityIcon,
  update_label: TaskHistoryFieldtaskIcon,
  update_description: TaskHistoryFieldtaskIcon,
  change_dates: TaskHistoryDateIcon,
  start_timer: TaskHistoryTimerIcon,
  stop_timer: TaskHistoryTimerIcon,
  update_timer: TaskHistoryTimerIcon,
  add_custom_field: TaskHistoryFieldtaskIcon,
  remove_custom_field: TaskHistoryFieldtaskIcon,
  update_custom_field_label: TaskHistoryFieldtaskIcon,
  update_custom_field_value: TaskHistoryFieldtaskIcon,
  edit_task_name: EditTaskNameIcon,
  move_to_file: MoveToFileIcon,
  upvote: UpvoteIcon,
  remove_task: RemoveTaskIcon,
};

export const TaskHistory = ({ task }: { task: Partial<Task>; OnClickBackArrow?: () => void }) => {
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [historyData, setHistoryData] = useState<any>([]);

  useEffect(() => {
    const fetchHistory = async () => {
      const response = await API.get(`/board/task/history?id=${task.id}&page=${page}&limit=10`);
      const { docs, hasNextPage } = response.data.data;
      setHasMore(hasNextPage);

      setHistoryData((prev) => {
        prev.push(...docs);
        return [...prev];
      });
    };
    fetchHistory();
  }, [page]);

  const handleFetchHistoryData = () => {
    setPage(page + 1);
  };

  const getHistoryItemComponent = React.useCallback((data) => {
    switch (data.message.type) {
      case 'move_to_column':
        return <Move_history data={data} />;
      case 'add_task':
        return <Task_history data={data} />;
      case 'update_priority':
        return <Priority_history data={data} />;
      case 'change_dates':
        return <Date_history data={data} />;
      case 'add_attachment':
        return <Attachment_history data={data} />;
      case 'assigner':
        return <Assigner_history data={data} />;
      case 'add_assignee':
      case 'remove_assignee':
        return <Assignee_history data={data} />;
      case 'add_comment':
      case 'edit_comment':
      case 'remove_comment':
        return <Comment_history data={data} />;
      case 'update_label':
      case 'update_description':
      case 'edit_task_name':
      case 'field':
        return <Field_history data={data} />;
      case 'start_timer':
      case 'stop_timer':
      case 'update_timer':
        return <Timer_history data={data} />;
      case 'add_custom_field':
      case 'remove_custom_field':
      case 'update_custom_field_label':
      case 'update_custom_field_value':
        return <CustomField_history data={data} />;
      case 'remove_subtask':
      case 'add_subtask':
        return <Subtask_history data={data} />;
      case 'complete':
      case 'complete_subtask':
        return <Completion_history data={data} />;
      case 'move_to_file':
      case 'upvote':
      case 'remove_task':
      default:
        return <Completion_history data={data} />;
    }
  }, []);

  return (
    <div className="flex flex-col overflow-none">
      <div
        className={cn('overflow-hidden overflow-y-scroll no-scrollbar h-full')}
        id="scrollableDiv"
      >
        <InfiniteScroll
          dataLength={historyData.length}
          next={handleFetchHistoryData}
          hasMore={hasMore}
          scrollableTarget="scrollableDiv"
          loader={
            <div>
              <LoaderXs className="mx-auto text-xss m-10" />
            </div>
          }
          endMessage={
            <div className="flex items-center justify-center pt-10 pb-10">
              <StandardSnack text="That’s all, folks!" />
            </div>
          }
        >
          {historyData &&
            historyData.map((data, index) => {
              const showConnectorBorder = index !== historyData.length - 1;
              const Icon = HistoryTypeIcon[data.message.type] || DefaultIcon;
              return (
                <>
                  <div key={index} className="flex gap-10 w-full min-h-90 items-stretch">
                    <div className="flex-shrink-0">
                      <div className="flex flex-col justify-items-center h-full self-stretch">
                        <Icon className="flex" />
                        {showConnectorBorder ? (
                          <span className="w-1 pl-16 text-gray-200 border-r h-full flex-grow"></span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-col flex-grow min-w-0">
                      <span className="flex w-full justify-between">
                        <span className="flex-col text-gray-999 text-sm">{data.message.name}</span>
                        <span className="flex text-gray-500 text-xs min-w-0 flex-shrink-0">
                          {format(parseISO(data.createdAt), 'dd MMM | HH:mm')}
                        </span>
                      </span>
                      {getHistoryItemComponent(data)}
                    </div>
                  </div>
                </>
              );
            })}
        </InfiniteScroll>
      </div>
    </div>
  );
};

const Subtask_history = ({ data }: { data: any }) => {
  return <p className="flex w-209 text-xs title">{data.message.text}</p>;
};

const Attachment_history = ({ data }: { data: any }) => {
  const extension = data.message.attachment && data.message.attachment.split('.').pop();
  return (
    <>
      <p className="flex w-209 text-xs title">{data.message.text}</p>
      <span>
        {data.message.url && (
          <div className="flex flex-row w-156 h-40 mt-2 border rounded-md bg-gray-100 items-center">
            {extension === 'jpeg' || extension === 'png' ? (
              <img
                className="max-w-32 max-h-32 w-32 h-32 object-fill rounded-md ml-4"
                src={data.message.url}
              />
            ) : (
              <div className="flex max-w-32 max-h-32 min-w-32 min-h-32 bg-gray-300 rounded-md place-content-center ml-4">
                <div className="flex text-blue-500 text-xss font-medium items-center">
                  {extension && extension.toUpperCase()}
                </div>
              </div>
            )}
            <p className="text-sm font-regular text-gray-999 pl-4 pr-6 truncate">
              {data.message.attachment?.substr(0, 25)}
            </p>
          </div>
        )}
      </span>
    </>
  );
};

const Field_history = ({ data }: { data: any }) => {
  return <p className="flex w-209 text-xs title">{data.message.text}</p>;
};

const Assignee_history = ({ data }: { data: any }) => {
  return <p className="flex w-209 text-xs title">{data.message.text}</p>;
};
const Assigner_history = ({ data }: { data: any }) => {
  return <p className="flex w-209 text-xs title">{data.message.text}</p>;
};

const Completion_history = ({ data }: { data: any }) => {
  return data ? <p className="flex w-209 text-xs title">{data.message.text}</p> : null;
};

const Move_history = ({ data }: { data: any }) => {
  return data ? <p className="flex w-209 text-xs title">{data.message.text}</p> : null;
};

const Task_history = ({ data }: { data: any }) => {
  return <div className="flex w-209 text-xs title">{data.message.text}</div>;
};
const Priority_history = ({ data }: { data: any }) => {
  return <div className="flex w-209 text-xs title">{data.message.text}</div>;
};
const Comment_history = ({ data }: { data: any }) => {
  return <div className="flex w-209 text-xs title">{data.message.text}</div>;
};

const Date_history = ({ data }: { data: any }) => {
  return <p className="flex w-209 text-xs title">{data.message.text}</p>;
};

const Timer_history = ({ data }: { data: any }) => {
  return <p className="flex w-209 text-xs title">{data.message.text}</p>;
};

const CustomField_history = ({ data }: { data: any }) => {
  return <p className="flex w-209 text-xs title">{data.message.text}</p>;
};
